import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  Observable,
} from 'rxjs';
import { makePermissionsComplete } from 'server/helpers/permissions/permissions';
import { Users } from 'server/shared/collections/users';
import { RoleEnum } from 'server/shared/models/role.enum';
import {
  DispoUser,
  PoolerUser,
  RequesterUser,
  User,
} from 'server/shared/models/user';
import { UserAvailability } from 'server/shared/models/userAvailability';
import { rxJsonClone } from 'src/helpers/rxJsonClone';
import {
  InviteeAvailableJob,
  InviteeAvailableJobAction,
  InviteeAvailableJobsDialogComponent,
} from '../shared/invitee-available-jobs-dialog/invitee-available-jobs-dialog.component';
import { SubscriptionService } from '../shared/subscription.service';
import { ToastService } from './toast/toast.service';
@Injectable({
  providedIn: 'root',
})
export class UserService {
  user$ = new BehaviorSubject<User | undefined>(undefined);
  readyUser$ = this.user$.pipe(
    // check for email is needed to not get an empty meteor user
    filter((user): user is User => !!(user && user.email)),
  );
  isLoggedIn$ = this.user$.pipe(map((user) => !!(user && user.email)));

  role$ = this.readyUser$.pipe(
    map((user) => user.role),
    distinctUntilChanged(),
  );
  role = firstValueFrom(this.role$);

  isPooler$ = this.role$.pipe(map((role) => role === RoleEnum.POOL));
  isDispo$ = this.role$.pipe(map((role) => role === RoleEnum.DISPO));
  isRequester$ = this.role$.pipe(map((role) => role === RoleEnum.REQUESTER));
  isAdmin$ = this.readyUser$.pipe(
    map((user) => user.role === RoleEnum.DISPO && !!user.isAdmin),
  );

  readyDispo$ = this.readyUser$.pipe(
    filter((user): user is DispoUser => user.role === RoleEnum.DISPO),
  );

  // used so that "recentUsed" updates don't trigger permission subscriptions
  dispoPermissions$ = this.readyDispo$.pipe(
    map((user) => makePermissionsComplete(user.dispoPermissions)),
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
    rxJsonClone(),
  );

  readyPooler$ = this.readyUser$.pipe(
    filter((user): user is PoolerUser => user.role === RoleEnum.POOL),
  );

  readyRequester$ = this.readyUser$.pipe(
    filter((user): user is RequesterUser => user.role === RoleEnum.REQUESTER),
  );

  readyDispoOrRequester$ = this.readyUser$.pipe(
    filter(
      (user): user is DispoUser | RequesterUser =>
        user.role === RoleEnum.DISPO || user.role === RoleEnum.REQUESTER,
    ),
  );

  constructor(
    private subscriptionService: SubscriptionService,
    private dialog: MatDialog,
    private toastService: ToastService,
  ) {
    Tracker.autorun(() => {
      let user = Users.findOne(Meteor.userId());
      this.user$.next(user || undefined);
    });
  }

  getDispoById(dispoId: string): Observable<DispoUser> {
    return this.subscriptionService.dispoUsersOfCustomer.ready$.pipe(
      map(() => {
        const dispoUser = Users.findOne({
          _id: dispoId,
          role: RoleEnum.DISPO,
        }) as DispoUser;
        if (!dispoUser) {
          throw new Error(
            'Could not fetch dispoUser by id, probably the subscription is not finished',
          );
        }
        return dispoUser;
      }),
    );
  }

  updateAvailability(
    userId: string,
    availability: UserAvailability,
    skipOpenJobsCheck = false,
  ): Promise<{ availabilityUpdated: boolean }> {
    return new Promise((resolve, reject) => {
      Meteor.call(
        'updateAvailability',
        userId,
        availability,
        skipOpenJobsCheck,
        (
          error: Meteor.Error,
          result: { success: boolean; openJobs?: InviteeAvailableJob[] },
        ) => {
          if (error) {
            console.log(error);
            reject(error);
            return;
          }
          if (
            result &&
            !result.success &&
            result.openJobs &&
            result.openJobs.length > 0
          ) {
            // Open dialog to show available jobs and ask for confirmation
            const dialogRef = this.dialog.open(
              InviteeAvailableJobsDialogComponent,
              {
                data: { openJobs: result.openJobs },
              },
            );
            dialogRef
              .afterClosed()
              .subscribe((response?: InviteeAvailableJobAction) => {
                if (response === 'cancel-jobs') {
                  // User confirmed to cancel the jobs
                  this.cancelInviteeAvailableJobs(userId, result.openJobs!)
                    .then(() => {
                      this.updateAvailability(userId, availability, true).then(
                        () => {
                          resolve({ availabilityUpdated: true });
                        },
                      );
                    })
                    .catch((error) => {
                      console.error('Error canceling jobs:', error);
                      resolve({ availabilityUpdated: false });
                    });
                } else if (response === 'keep-open') {
                  // User chose not to cancel the jobs
                  this.updateAvailability(userId, availability, true).then(
                    () => {
                      resolve({ availabilityUpdated: true });
                    },
                  );
                } else {
                  resolve({ availabilityUpdated: false });
                }
              });
          } else {
            resolve({ availabilityUpdated: true });
          }
        },
      );
    });
  }

  private cancelInviteeAvailableJobs(
    userId: string,
    jobs: InviteeAvailableJob[],
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      // Extract job IDs from the jobs array
      const jobIds = jobs.map((job) => job._id);

      // Call the server method to cancel the jobs
      Meteor.call(
        'cancelInviteeAvailableJobs',
        userId,
        jobIds,
        (error: Meteor.Error, result: { success: boolean }) => {
          if (error) {
            console.error('Error canceling jobs:', error);
            reject(error);
            return;
          }

          if (result && result.success) {
            this.toastService.add({
              type: 'SUCCESS',
              message: 'xJobsDeclined',
              messageOptions: {
                count: jobs.length.toString(),
              },
            });
            resolve();
          } else {
            reject(new Error('Failed to cancel jobs'));
          }
        },
      );
    });
  }
}
